import React, { ReactElement } from "react";
import {
  CornerRadius,
  FoundationColorTokens,
  Spacing,
  Typography as LeoTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import {
  BorderStyle,
  DeveloperErrorFlow,
  RICH_TEXT_MARKDOWN_PROPS,
  richTextTypography,
} from "@pulse/shared-components";
import {
  DataProps,
  DataPropsForGridStackedAndGroupedBar,
} from "../DataSection";
import { ProportionGraph } from "../graphs/ProportionGraph";
import { CustomWordcloud } from "../graphs/CustomWordcloud";
import { StackedColumnGroupedBarGraph } from "../graphs/StackedColumnGroupedBarGraph";
import { Stack, Typography } from "@mui/material";
import { RichTextEditor } from "@surya-digital/leo-reactjs-remirror";
import { TFunction } from "i18next";
import { QuestionTypeGraphs } from "../ChartSelection";
import {
  CHILD_QUESTION_VIEW_MAX_WIDTH_IN_PIXELS,
  QUARTILE_COLORS,
} from "../../../utils/constants";

interface GridSelectedGraphProps {
  selectedGraph: QuestionTypeGraphs;
  data: DataPropsForGridStackedAndGroupedBar[] | DataProps[][] | DataProps[];
  isNeutral?: boolean;
  spacing: Spacing;
  cornerRadius: CornerRadius;
  tokens: FoundationColorTokens<string>;
  typography: LeoTypography;
  border: BorderStyle;
  gridSingleSelectRowOptions: string[] | undefined;
  t: TFunction;
  isChildQuestion: boolean;
  isSurveyViewOptionDisplayTextSelected: boolean;
  naOptionCode?: string | null;
}

export const GridSelectedGraph = ({
  selectedGraph,
  data,
  isNeutral,
  spacing,
  cornerRadius,
  tokens,
  typography,
  border,
  gridSingleSelectRowOptions,
  t,
  isChildQuestion,
  isSurveyViewOptionDisplayTextSelected,
  naOptionCode = null,
}: GridSelectedGraphProps): ReactElement => {
  const getMeanTextColor = (quartileColor: string): string => {
    switch (quartileColor) {
      case QUARTILE_COLORS.cardinalRed:
      case QUARTILE_COLORS.neutralGrey:
      case QUARTILE_COLORS.deepGreen:
        return tokens.labelOnSurface;
      case QUARTILE_COLORS.amber:
      case QUARTILE_COLORS.limeGreen:
      default:
        return tokens.label;
    }
  };
  switch (selectedGraph) {
    case QuestionTypeGraphs.PROPORTION: {
      const isMeanPresent = (index: number): boolean => {
        return Boolean(
          Array.isArray(data[index]) &&
            data[index].length > 0 &&
            data[index][0].meanProps?.mean,
        );
      };
      return (
        <Stack>
          {data.map((dataForGraph, index) => (
            <Stack key={index}>
              {gridSingleSelectRowOptions && (
                <Stack direction="row" justifyContent="space-between">
                  <RichTextEditor
                    name={gridSingleSelectRowOptions[index]}
                    typography={richTextTypography()}
                    mode="preview"
                    borderOnPreview={false}
                    initialValue={gridSingleSelectRowOptions[index]}
                    supports={RICH_TEXT_MARKDOWN_PROPS}
                  />
                  {isMeanPresent(index) && (
                    <Typography
                      {...typography.b2}
                      color={tokens.label}
                      paddingRight={spacing.spaceXL}
                    >
                      {t("surveyDashboard.mean")}
                    </Typography>
                  )}
                </Stack>
              )}
              <Stack
                direction="row"
                width="100%"
                height="fit-content"
                justifyContent="center"
                alignItems="flex-start"
                gap={spacing.spaceSM}
              >
                <ProportionGraph
                  data={
                    naOptionCode !== null
                      ? (dataForGraph as DataProps[]).filter(
                          (item) => item.id !== naOptionCode,
                        )
                      : (dataForGraph as DataProps[])
                  }
                  isNeutral={isNeutral}
                  spacing={spacing}
                  cornerRadius={cornerRadius}
                  tokens={tokens}
                  border={border}
                  typography={typography}
                  isLegendsVisible={data.length === index + 1}
                  graphWidth={isMeanPresent(index) ? 820 : undefined}
                />
                {
                  // We cannot use `isMeanPresent(index)` here as the condition is required here for type safety.
                  Array.isArray(dataForGraph) &&
                    dataForGraph.length > 0 &&
                    dataForGraph[0].meanProps?.mean && (
                      <Stack
                        height="100%"
                        width="100%"
                        gap={spacing.spaceSM}
                        paddingTop={spacing.space2XL}
                      >
                        <Stack
                          justifyContent="center"
                          height="106px"
                          width="72px"
                          padding={spacing.spaceXS}
                          sx={{
                            backgroundColor:
                              dataForGraph[0].meanProps.gridQuartileColor ??
                              tokens.backgroundSubtle,
                          }}
                        >
                          <Typography
                            {...typography.sh4}
                            color={getMeanTextColor(
                              dataForGraph[0].meanProps.gridQuartileColor ??
                                tokens.backgroundSubtle,
                            )}
                          >
                            {dataForGraph[0].meanProps.mean}
                          </Typography>
                        </Stack>
                      </Stack>
                    )
                }
              </Stack>
            </Stack>
          ))}
        </Stack>
      );
    }
    case QuestionTypeGraphs.WORD_CLOUD: {
      return <CustomWordcloud graphData={data as DataProps[]} />;
    }
    case QuestionTypeGraphs.STACKED_COLUMN: {
      return (
        <StackedColumnGroupedBarGraph
          data={data as DataPropsForGridStackedAndGroupedBar[]}
          spacing={spacing}
          cornerRadius={cornerRadius}
          tokens={tokens}
          border={border}
          typography={typography}
          isStackedBarGraph={true}
          graphWidth={
            isChildQuestion
              ? CHILD_QUESTION_VIEW_MAX_WIDTH_IN_PIXELS
              : undefined
          }
          t={t}
          isSurveyViewOptionDisplayTextSelected={
            isSurveyViewOptionDisplayTextSelected
          }
        />
      );
    }
    case QuestionTypeGraphs.GROUPED_BAR: {
      return (
        <StackedColumnGroupedBarGraph
          data={data as DataPropsForGridStackedAndGroupedBar[]}
          spacing={spacing}
          cornerRadius={cornerRadius}
          tokens={tokens}
          border={border}
          typography={typography}
          isStackedBarGraph={false}
          graphWidth={
            isChildQuestion
              ? CHILD_QUESTION_VIEW_MAX_WIDTH_IN_PIXELS
              : undefined
          }
          t={t}
          isSurveyViewOptionDisplayTextSelected={
            isSurveyViewOptionDisplayTextSelected
          }
        />
      );
    }
    case QuestionTypeGraphs.NONE: {
      return <></>;
    }
    case QuestionTypeGraphs.DONUT:
    case QuestionTypeGraphs.GAUGE:
    case QuestionTypeGraphs.HORIZONTAL_BAR:
    case QuestionTypeGraphs.LINE:
    case QuestionTypeGraphs.LINE_AREA:
    case QuestionTypeGraphs.PIE:
    case QuestionTypeGraphs.VERTICAL_BAR:
    case QuestionTypeGraphs.PROPORTION_WITH_HORIZONTAL_BAR:
    case QuestionTypeGraphs.PROPORTION_WITH_VERTICAL_BAR: {
      return <DeveloperErrorFlow />;
    }
  }
};
